import React, { useState, useRef } from 'react';
import { Box, TextField, Typography, Alert } from '@mui/material';

export default function MFACodeInput({ onCodeComplete, error }) {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if all digits are entered
    if (newCode.every(digit => digit !== '') && index === 5) {
      const fullCode = newCode.join('');
      onCodeComplete(fullCode);
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        // Move to previous input if current is empty
        inputRefs.current[index - 1]?.focus();
      } else {
        // Clear current input
        const newCode = [...code];
        newCode[index] = '';
        setCode(newCode);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    // Only accept 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
      onCodeComplete(pastedData);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', my: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter the 6-digit code from your authenticator app
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'center',
          mb: 2
        }}
      >
        {code.map((digit, index) => (
          <TextField
            key={index}
            inputRef={ref => inputRefs.current[index] = ref}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            inputProps={{
              maxLength: 1,
              style: {
                textAlign: 'center',
                fontSize: '24px',
                fontWeight: 'bold'
              }
            }}
            sx={{
              width: '50px',
              '& input': {
                padding: '12px'
              }
            }}
            autoFocus={index === 0}
            variant="outlined"
          />
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Tip: You can paste the full code
      </Typography>
    </Box>
  );
}
